import {
	type FileConfigDTO,
	FileConfigFieldErrorCode,
	type LogicalFileType,
	Permission,
	RetrievalMethodType,
	TenantConfigPlane,
	type WebUploadMethod,
} from '@today/types'
import { FILE_TYPE_LABELS, FileExchangeFileForm, FileExchangeFileList, ImportUploadPanel } from '@today/ui-domain'
import { useState } from 'react'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { type FileExchangeSaveError, useFileExchangeFiles } from '@/hooks/useFileExchangeFiles'
import { useImportUpload } from '@/hooks/useImportUpload'
import type { TenantConfigSectionProps } from '../section-props'
import { ImportDefinitionsPanel } from './import-definitions/ImportDefinitionsPanel'
import { SftpConnectionsSection } from './SftpConnectionsSection'

const FIELD_MESSAGES: Record<FileConfigFieldErrorCode, string> = {
	[FileConfigFieldErrorCode.REQUIRED]: 'Required.',
	[FileConfigFieldErrorCode.UNKNOWN_FIELD]: 'Not accepted.',
	[FileConfigFieldErrorCode.INVALID_TYPE]: 'Wrong kind of value.',
	[FileConfigFieldErrorCode.INVALID_FORMAT]: 'Not in the expected format.',
	[FileConfigFieldErrorCode.INVALID_VALUE]: 'Not one of the allowed values.',
	[FileConfigFieldErrorCode.OUT_OF_RANGE]: 'Out of the allowed range.',
	[FileConfigFieldErrorCode.DUPLICATE]: 'Repeated.',
	[FileConfigFieldErrorCode.NOT_ALLOWED]: 'Not allowed with the other settings.',
	[FileConfigFieldErrorCode.INCONSISTENT]: 'Does not fit the related fields — check it against them.',
}

/** The field-level messages a refusal puts next to its fields, keyed by API path. */
const fieldErrorsOf = (error: FileExchangeSaveError | null): Record<string, string> => {
	if (error?.kind === 'invalid') {
		return Object.fromEntries(error.fields.map(({ path, code }) => [path, FIELD_MESSAGES[code]]))
	}
	if (error?.kind === 'users-jit') {
		return {
			enabled: `The users file needs invitation provisioning, but the provisioning mode is ${error.provisioningMode}. Change it to INVITE in Identity & login before enabling the users file.`,
		}
	}
	return {}
}

const formErrorOf = (error: FileExchangeSaveError | null): string | null => {
	if (error?.kind === 'invalid') return 'Some fields need attention.'
	if (error?.kind === 'conflict') return 'This file was changed by someone else. Reload to see the latest version.'
	if (error?.kind === 'failed') return 'The settings could not be saved — try again.'
	return null
}

/** The open file's `web` method, when it has one. */
const webMethodOf = (file: FileConfigDTO): WebUploadMethod | undefined =>
	file.retrieval.methods.find((method): method is WebUploadMethod => method.type === RetrievalMethodType.WEB)

/** TO-392: uploads are own-tenant only (Q8), for a viewer holding `file_exchange.upload`. */
const useCanUpload = (plane: TenantConfigPlane): boolean => {
	const { permissions } = useViewerPermissions()
	return plane === TenantConfigPlane.TENANT && permissions.includes(Permission.FILE_EXCHANGE_UPLOAD)
}

/** The drop zone of one file's `web` upload (TO-392 REQ-014). */
function FileUpload({ fileType, method }: { fileType: LogicalFileType; method: WebUploadMethod }) {
	const { state, statusText, message, upload } = useImportUpload(fileType)
	return (
		<ImportUploadPanel
			fileLabel={FILE_TYPE_LABELS[fileType]}
			filenamePattern={method.filenamePattern}
			state={state}
			statusText={statusText}
			message={message}
			onFile={(file) => void upload(file)}
		/>
	)
}

function LoadFailed({ onRetry }: { onRetry: () => void }) {
	return (
		<div role="alert" className="flex flex-col items-start gap-3 text-sm text-ds-danger">
			<p>Could not load the file exchange settings.</p>
			<button type="button" onClick={onRetry} className="rounded-md border px-3 py-1">
				Try again
			</button>
		</div>
	)
}

/**
 * The console's `file-exchange` section (TO-391 REQ-015): the tenant's five
 * logical files and the editor of the one opened. Both planes; the route tree
 * follows the plane, and READ access shows the files without a save. On the
 * platform plane it also holds the Super-Admin's SFTP connections (TO-393).
 */
export function FileExchangeSection({ tenantId, plane, readOnly }: TenantConfigSectionProps) {
	const { files, loadFailed, reload, save, saving, saveError, clearSaveError } = useFileExchangeFiles(tenantId, plane)
	const canUpload = useCanUpload(plane)
	const [selected, setSelected] = useState<LogicalFileType | null>(null)

	if (loadFailed) return <LoadFailed onRetry={reload} />
	if (!files) {
		return (
			<p className="text-sm text-ds-text-muted" aria-live="polite">
				Loading file exchange settings…
			</p>
		)
	}

	const open = files.find((file) => file.fileType === selected)
	const webMethod = open && canUpload ? webMethodOf(open) : undefined
	const select = (fileType: LogicalFileType) => {
		clearSaveError()
		setSelected(fileType)
	}
	const reloadLatest = () => {
		clearSaveError()
		reload()
	}

	return (
		<div className="flex flex-col gap-6">
			<FileExchangeFileList files={files} selected={selected} onSelect={select} />
			{open ? (
				<FileExchangeFileForm
					key={`${open.fileType}:${open.version}`}
					file={open}
					readOnly={readOnly}
					isBusy={saving}
					fieldErrors={fieldErrorsOf(saveError)}
					error={formErrorOf(saveError)}
					onReload={saveError?.kind === 'conflict' ? reloadLatest : undefined}
					onSave={(fields) => save(open.fileType, open.version, fields)}
				/>
			) : null}
			{open && webMethod ? <FileUpload key={open.fileType} fileType={open.fileType} method={webMethod} /> : null}
			{plane === TenantConfigPlane.PLATFORM ? <SftpConnectionsSection tenantId={tenantId} /> : null}
			<ImportDefinitionsPanel tenantId={tenantId} plane={plane} readOnly={readOnly} />
		</div>
	)
}
